import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ChangePasswordModal({ onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({ current: false, next: false, confirm: false });

  const schema = z
    .object({
      currentPassword: z.string().min(1, t('profile.changePassword.errRequired')),
      newPassword: z.string().min(8, t('profile.changePassword.errMinChars')),
      confirmPassword: z.string().min(1, t('profile.changePassword.errRequired')),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      message: t('profile.changePassword.errMismatch'),
      path: ['confirmPassword'],
    });
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  async function onSubmit(data: FormValues) {
    setError('');
    setLoading(true);
    try {
      await api.patch('/profile/password', { currentPassword: data.currentPassword, newPassword: data.newPassword });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setLoading(false);
    }
  }

  const inputCls = 'h-10 w-full rounded-lg border border-ws-border bg-ws-control px-3 pr-10 text-sm text-ws-text placeholder:text-ws-muted';
  const labelCls = 'text-xs font-semibold uppercase tracking-wide text-ws-muted';

  const field = (labelKey: string, name: keyof FormValues, placeholderKey: string, showKey: 'current' | 'next' | 'confirm') => (
    <div className='flex flex-col gap-1.5'>
      <label htmlFor={name} className={labelCls}>{t(labelKey)}</label>
      <div className='relative'>
        <input
          id={name}
          type={show[showKey] ? 'text' : 'password'}
          placeholder={t(placeholderKey)}
          aria-invalid={!!errors[name]}
          aria-describedby={errors[name] ? `${name}-err` : undefined}
          className={inputCls}
          {...register(name)}
        />
        <button
          type='button'
          onClick={() => setShow((s) => ({ ...s, [showKey]: !s[showKey] }))}
          tabIndex={-1}
          aria-label={show[showKey] ? t('common.hide') : t('common.show')}
          className='absolute right-3 top-1/2 -translate-y-1/2 text-ws-muted hover:text-ws-text'
        >
          {show[showKey] ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
        </button>
      </div>
      {errors[name] && <p id={`${name}-err`} className='text-xs text-ws-error-fg'>{errors[name]?.message}</p>}
    </div>
  );

  return (
    <ModalShell title={t('profile.changePassword.title')} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
        {field('profile.changePassword.currentPassword', 'currentPassword', 'profile.changePassword.currentPasswordPlaceholder', 'current')}
        {field('profile.changePassword.newPassword', 'newPassword', 'profile.changePassword.newPasswordHint', 'next')}
        {field('profile.changePassword.confirmPassword', 'confirmPassword', 'profile.changePassword.confirmPasswordPlaceholder', 'confirm')}
        {error && <p role='alert' className='text-sm text-ws-error-fg'>{error}</p>}
        <div className='flex items-center justify-end gap-3'>
          <Button size='lg' onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant='primary' size='lg' type='submit' disabled={loading}>
            {loading ? t('profile.changePassword.submitting') : t('profile.changePassword.submit')}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
